import type { FC } from 'react'
import { useState } from 'react'

import { Popup, Input } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import CartCheckIcon from 'mdi-react/CartCheckIcon'
import CheckboxMarkedCircleOutlineIcon from 'mdi-react/CheckboxMarkedCircleOutlineIcon'
import CloseIcon from 'mdi-react/CloseThickIcon'
import FormatListChecksIcon from 'mdi-react/FormatListChecksIcon'
import PackageVariantIcon from 'mdi-react/PackageVariantIcon'
import SortNumericAscendingIcon from 'mdi-react/SortNumericAscendingIcon'
import SortNumericDescendingIcon from 'mdi-react/SortNumericDescendingIcon'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useGroupedProductList, usePolicyId } from '@/modules/ecom-policy/policy/hooks'
import { usePolicyFilters } from '@/modules/ecom-policy/policy/Policy.context'
import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'
import handleMultipleChoiceItemSelect from '@/util/handleMultipleChoiceItemSelect'
import { SORT_ORDER } from '@/util/misc'

import PolicyProductCategories from './Categories'
import { ProductGroupOverview } from './ProductGroupOverview'

import './product-filter.scss'

const Filters: FC = () => {
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false)
  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false)

  const policyId = usePolicyId()
  const translate = useTranslation()
  const list = useGroupedProductList()

  const filters = usePolicyFilters()
  const [searchQuery, setSearchQuery] = filters.searchQuery
  const [showOnlyInStock, setShowOnlyInStock] = filters.stockFilter
  const [showOnlySelected, setShowOnlySelected] = filters.onlySelectedFilter
  const [showOnlyMandatory, setShowOnlyMandatory] = filters.onlyMandatoryFilter
  const [sortParam, setSortParam] = filters.sortParam
  const [sortOrder, setSortOrder] = filters.sortOrder
  const [selectedCategoryIds, selectCategoryIds] = filters.selectedCategoryIds
  const [currentlyBrowsedGroupKey, setCurrentlyBrowsedGroupKey] = filters.currentlyBrowsedGroupKey
  const { productsWithVisibleAccessoriesIds, setProductsWithVisibleAccessoriesIds } = filters

  const { data: policy } = useQuery(queries.policyDetails(policyId), () =>
    ecomPolicyResources.getPolicyDetails(policyId)
  )

  const handleProductModalClose = () => {
    setIsProductsModalOpen(false)
    setCurrentlyBrowsedGroupKey(null)
  }

  const toggleSorting = () => {
    if (sortParam === 'isSelected') {
      setSortParam('priceFrom')
      return
    }

    if (sortParam === 'priceFrom' && sortOrder === SORT_ORDER.DESC) {
      setSortOrder('asc')
      return
    }

    setSortOrder('desc')
    setSortParam('isSelected')
  }

  const isCategoryFilterActive = selectedCategoryIds && selectedCategoryIds?.length > 0
  const productsModalData =
    currentlyBrowsedGroupKey && list.groupedAndFiltered[currentlyBrowsedGroupKey]
  const hasVat = policy?.hasVat

  return (
    <div className="ecom-policy__product-filter">
      <Input
        id="policy-product-search"
        type="text"
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        placeholder={translate('Search')}
      />

      <div className="input-container sorting" title={translate('Sort by price')}>
        <button
          className={`${sortParam === 'priceFrom' ? 'selected' : ''} ${'filter-button'}`}
          onClick={toggleSorting}
        >
          {sortOrder === SORT_ORDER.ASC ? (
            <SortNumericAscendingIcon />
          ) : (
            <SortNumericDescendingIcon />
          )}
        </button>
      </div>

      <div className="input-container stock" title={translate('Show only products in stock')}>
        <button
          className={`${showOnlyInStock ? 'selected' : ''} ${'filter-button'}`}
          onClick={() => setShowOnlyInStock(!showOnlyInStock)}
        >
          <PackageVariantIcon />
        </button>
      </div>

      <div className="input-container selected" title={translate('Show only selected products')}>
        <button
          className={`${showOnlySelected ? 'selected' : ''} ${'filter-button'}`}
          onClick={() => setShowOnlySelected(!showOnlySelected)}
        >
          <CheckboxMarkedCircleOutlineIcon />
        </button>
      </div>

      <div className="input-container mandatory" title={translate('Show only mandatory products')}>
        <button
          className={`${showOnlyMandatory ? 'mandatory' : ''} ${'filter-button'}`}
          onClick={() => setShowOnlyMandatory(!showOnlyMandatory)}
        >
          <CartCheckIcon />
        </button>
      </div>

      {Boolean(list.availableCategories.length) && (
        <div className="input-container categories" title={translate('Apply category filter')}>
          <button
            onClick={() => setIsCategoriesModalOpen(!isCategoriesModalOpen)}
            className={`${isCategoryFilterActive ? 'selected' : ''} ${'filter-button'}`}
          >
            <FormatListChecksIcon />
          </button>
        </div>
      )}

      {productsWithVisibleAccessoriesIds && (
        <Button
          className="mb-0 ml-3 md:mt-0"
          Icon={<CloseIcon />}
          onClick={() => {
            setProductsWithVisibleAccessoriesIds(null)
            setIsProductsModalOpen(true)
          }}
        >
          <Translation id="Remove accessory filter" />
        </Button>
      )}

      <Popup
        isOpen={isCategoriesModalOpen}
        onClose={() => setIsCategoriesModalOpen(false)}
        ariaLabel={translate('Filter by categories')}
      >
        <PolicyProductCategories
          selectedCategoryIds={selectedCategoryIds}
          availableCategories={list.availableCategories}
          onCategorySelect={(selectedId: number) =>
            handleMultipleChoiceItemSelect({
              selectedId,
              currentIdList: selectedCategoryIds,
              setData: selectCategoryIds,
            })
          }
        />
      </Popup>

      {productsModalData && hasVat !== undefined && (
        <Popup
          isOpen={isProductsModalOpen}
          ariaLabel={translate('Modal with products')}
          variant="wide"
          onClose={handleProductModalClose}
        >
          <ProductGroupOverview
            groupKey={productsModalData.groupKey}
            groupName={productsModalData.groupName}
            groupedProducts={productsModalData.groupedProducts}
            description={productsModalData.description}
            hasAccessories={productsModalData.hasAccessories}
            isMandatory={productsModalData.isMandatory}
            onModalClose={handleProductModalClose}
          />
        </Popup>
      )}
    </div>
  )
}

export default Filters
